import React, { Component, ErrorInfo, ReactNode } from 'react';
import Modal from '../Modal';
import Button from '../Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const errorDetails = this.state.error?.toString() || 'Unknown error occurred';
      const stackTrace = this.state.errorInfo?.componentStack || '';

      const content = (
        <div className="d-error-page error-boundary">
          <i className="ri-error-warning-line ri-48px text-danger"></i>
          <h4>Something went wrong</h4>
          <p>
            An unexpected error occurred. Please try refreshing the page or contact support if the
            problem persists.
          </p>
          <div className="error-details mt-3">
            <details className="mt-2">
              <summary className="cursor-pointer text-primary">Show Error Details</summary>
              <pre
                className="mt-2 p-2 bg-light rounded"
                style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}
              >
                <strong>Error:</strong> {errorDetails}
                {stackTrace && (
                  <>
                    <br />
                    <br />
                    <strong>Component Stack:</strong>
                    <br />
                    {stackTrace}
                  </>
                )}
              </pre>
            </details>
          </div>
        </div>
      );

      const action = (
        <div className="d-flex gap-2">
          <Button
            text="Reload Page"
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          />
          <Button text="Try Again" className="btn btn-primary" onClick={this.handleReset} />
        </div>
      );

      return (
        <Modal
          open={true}
          onClose={() => {}}
          disableBackDrop={false}
          disableEscape={false}
          content={content}
          action={action}
          maxWidth="md"
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
